import { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import ModuleContext from '../../../_contexts/ModuleContext';
import AccountContext from '../../../../../contexts/AccountContext';

import Button, { ButtonProps } from '@pelckmans/elementis/components/button';
import ButtonDropdown from '@pelckmans/elementis/components/button-dropdown';
import SlideSetIcon from '@pelckmans/elementis/icons/material-presentation-present-modus';

import { ANALYTICS_EVENT_SUBLOCATIONS } from '../../../../../constants/analyticsEvents';
import { useSlideSetAnalytics } from '../../../../../hooks/useSlideSetAnalytics';
import useModuleAccess from '../../../../../hooks/useModuleAccess';
import { FEATURE_ACCESS } from '../../../../../core/access/module-access';
import { MaterialIcon } from '../../../components/MaterialIcon';
import useSlideSetApps from './hooks/useSlideSetApps';
import { buildDoceoUrl } from '../../../../../utils/slideSet';

export default function SlideSetAppButton({ size = 'md', emphasized }: Pick<ButtonProps, 'size' | 'emphasized'>) {
  const { module } = useContext(ModuleContext);
  const {
    account: { provider },
  } = useContext(AccountContext);
  const { t } = useTranslation();
  const { slideSets: sortedSlideSets } = useSlideSetApps();
  const { captureOpened } = useSlideSetAnalytics();
  const { appsAccess } = useModuleAccess(module);
  const appsAllowed = appsAccess === FEATURE_ACCESS.ALLOWED;

  if (!module) return null;
  if (sortedSlideSets.length === 0) return null;

  const tooltipText = !appsAllowed ? t('PAGES.TOC_LEVEL.APPS.SLIDE_SET.NO_LICENSE_TOOLTIP') : undefined;
  const subLocationId = module.isExerciseModule
    ? ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_EXERCISE_MODULE_MATERIAL
    : ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_MATERIAL;

  if (sortedSlideSets.length === 1) {
    return (
      <Button
        key="slide-set-button"
        as="link"
        startIcon={<SlideSetIcon data-testid="slide-set-icon" />}
        prominence="secondary"
        size={size}
        target="_blank"
        rel="noopener noreferrer"
        href={buildDoceoUrl(sortedSlideSets[0], provider)}
        tooltip={tooltipText}
        disabled={!appsAllowed}
        onClick={() => captureOpened(sortedSlideSets[0].id, subLocationId)}
        emphasized={emphasized}
      >
        {t('PAGES.TOC_LEVEL.APPS.SLIDE_SET.OPEN_SLIDE_SET')}
      </Button>
    );
  }
  return (
    <ButtonDropdown
      title={t('PAGES.TOC_LEVEL.APPS.SLIDE_SET.OPEN_SLIDE_SET')}
      key="slide-set-button"
      buttonProps={{
        prominence: 'secondary',
        startIcon: <SlideSetIcon data-testid="slide-set-icon" />,
        disabled: !appsAllowed,
      }}
      size={size}
      tooltip={tooltipText}
      disableBackgroundScroll
      emphasized={emphasized}
    >
      {sortedSlideSets.map(slideSet => {
        return (
          <ButtonDropdown.Item
            as="link"
            key={`${slideSet.id}-${slideSet.versionId}`}
            value={slideSet.id}
            text={slideSet.name}
            href={buildDoceoUrl(slideSet, provider)}
            target="_blank"
            rel="noopener noreferrer"
            textIcon={<MaterialIcon materialType="slide-set" isUserSlideSet={Boolean(slideSet.userId)} />}
            onClick={() => captureOpened(slideSet.id, subLocationId)}
          />
        );
      })}
    </ButtonDropdown>
  );
}
